import React from 'react';
import DataCard from '../common/DataCard';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import ImagePreview from '../common/ImagePreview';
import { MemberFirearmDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import IconButton from '../common/IconButton';
import { ExternalLink, Pen, Phone, RefreshCcw, Timer, Trash } from 'lucide-react';
import { getStatus, getRenewByInfo, getApplicationDaysInfo, statusLabel } from '../../utils/firearmUtils';
import { useNavigate } from 'react-router-dom';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';

interface MemberFirearmDataCardProps {
    data: MemberFirearmDTO[];
    handleEdit: (row: MemberFirearmDTO) => void;
    handleRewal: (row: MemberFirearmDTO) => void;
    handleDelete: (row: MemberFirearmDTO) => void;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;

    // Optional action renderers injected by the List page
    renderHeaderActions?: (row: MemberFirearmDTO) => React.ReactNode;
    renderPrimaryActions?: (row: MemberFirearmDTO) => React.ReactNode;
    renderSecondaryActions?: (row: MemberFirearmDTO) => React.ReactNode;
}

const MemberFirearmDataCard: React.FC<MemberFirearmDataCardProps> = ({
    data,
    handleEdit,
    handleRewal,
    handleDelete,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
    isLoading,
    renderHeaderActions,
    renderPrimaryActions,
    renderSecondaryActions,
}) => {

    const navigate = useNavigate();

    return (
        <div>
            {isLoading ? (
                <div className="py-8 flex justify-center"><Loader /></div>
            ) : data.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">No records found</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {data.map((mf) => {
                        const makeTitle = mf.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? mf.firearmMakeOther : mf.firearmMake?.title ?? 'Unknown';
                        const caliberTitle = mf.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? mf.caliberOther : mf.caliber?.title ?? 'Unknown';
                        const title = [makeTitle, caliberTitle].filter(Boolean).join(' - ') || 'Unknown Firearm';

                        const status = getStatus(mf);
                        const isGood = status === 'submitted' || status === 'active';
                        const falseVariant = status === 'expired' ? 'error' : 'warning'
                        const leftBadge = renderBooleanBadge(isGood, statusLabel[status], statusLabel[status], falseVariant);

                        const rightMeta =
                            status === 'submitted'
                                ? { label: 'Application Submitted', date: mf.applicationRenewalDate }
                                : status === 'active'
                                    ? { label: 'Expires', date: mf.licenceExpiryDate }
                                    : status === 'expiring'
                                        ? { label: 'Expiring', date: mf.licenceExpiryDate }
                                        : { label: 'Expired', date: mf.licenceExpiryDate };

                        const receiptId = mf.recieptFileId ?? (mf as any).receiptFileId; // tolerate legacy misspelling

                        return (
                            <DataCard<MemberFirearmDTO>
                                key={mf.id}
                                item={mf}
                                collapsibleOnMobile={true}
                                leftBadgeNode={() => leftBadge}
                                rightMeta={() => {
                                    const info = getRenewByInfo(mf.licenceExpiryDate);
                                    return {
                                        label: rightMeta.label,
                                        value: <span className={info.className}>
                                            {rightMeta.date ? convertToUserTime(String(rightMeta.date), true) : 'n/a'}
                                        </span>,
                                    };
                                }}
                                title={() => title}
                                headerActions={renderHeaderActions}
                                media={(row) =>
                                    row.firearmFileId ? (
                                        <div className="rounded-lg overflow-hidden">
                                            <ImagePreview fileId={row.firearmFileId} size="hlg" />
                                        </div>
                                    ) : null
                                }
                                fields={[
                                    {
                                        label: 'Description',
                                        hideIf: (row) => !row.description,
                                        render: (row) => row.description ?? ''
                                    },
                                    { label: 'Type', render: (row) => row.firearmType?.title ?? 'Unknown' },
                                    { label: 'Calibre', render: (row) => row.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.caliberOther : row.caliber?.title ?? 'Unknown' },
                                    { label: 'Action', render: (row) => row.firearmAction?.title ?? 'Unknown' },
                                    {
                                        label: 'Application Number',
                                        hideIf: (row) => row.applicationNumber == null,
                                        render: (row) => row.applicationNumber ?? 'Unknown'
                                    },
                                    {
                                        label: 'Application reference number received',
                                        hideIf: () => status != 'submitted',
                                        render: (row: MemberFirearmDTO) => {
                                            const info = getApplicationDaysInfo(row.applicationRenewalDate);
                                            return <span className={info.className}>{info.display}</span>;
                                        },
                                    },
                                    {
                                        label: 'Renew by',
                                        hideIf: () => status == 'submitted',
                                        render: (row: MemberFirearmDTO) => {
                                            const info = getRenewByInfo(row.licenceExpiryDate);
                                            return <span className={info.className}>{info.display}</span>;
                                        },
                                    },

                                ]}
                                files={[{ hideIf: () => receiptId == undefined, label: 'Receipt', id: receiptId }]}
                                primaryActions={
                                    renderPrimaryActions ??
                                    ((row) => {
                                        const appDaysInfo = getApplicationDaysInfo(row.applicationRenewalDate);
                                        return (
                                            <div className="flex justify-between w-full">
                                                <div>
                                                    {getStatus(row) === 'expired' && (
                                                        <IconButton
                                                            label='Contact Admin'
                                                            color="blue" icon={<Phone />}
                                                            onClick={() => navigate('/contact')}
                                                        />
                                                    )}
                                                    {getStatus(row) === 'submitted' && appDaysInfo.daysElapsed > 90 && (
                                                        <IconButton
                                                            label='Long Outstanding Application'
                                                            color="blue"
                                                            icon={<Timer />}
                                                            onClick={() => window.open('https://apps.natshoot.co.za/public-applications', '_blank')}
                                                        />
                                                    )}
                                                    {(getStatus(row) === 'active' || getStatus(row) === 'expiring') && (
                                                        <IconButton
                                                            label='Renew'
                                                            color="green"
                                                            icon={<RefreshCcw />}
                                                            onClick={() => handleRewal(row)} />
                                                    )}
                                                </div>
                                                <div className="flex gap-3">

                                                    <IconButton
                                                        label='Edit'
                                                        color="orange"
                                                        icon={<Pen />}
                                                        onClick={() => handleEdit(row)} />

                                                    <IconButton
                                                        label='Delete'
                                                        color="red"
                                                        icon={<Trash />}
                                                        onClick={() => handleDelete(row)} />
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                                secondaryActions={renderSecondaryActions}
                            />
                        );
                    })}
                </div>
            )}

            {!isLoading && data.length > 0 && (
                <div className="mt-4">
                    <Pagination
                        currentPage={Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1}
                        totalPages={Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1}
                        onPageChange={setCurrentPage}
                        pageSize={Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12}
                        onPageSizeChange={setPageSize}
                    />
                </div>
            )}
        </div>
    );
};

export default MemberFirearmDataCard;
